import { useEffect, useRef, useState } from 'react';
import type { Card, CamposCotacaoEtapa, Etapa, FornecedorCotacao } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { apiUrl } from '../../utils/apiBase';
import SeletorCamposCotacao from './SeletorCamposCotacao';

interface Fornecedor {
  nome: string;
  email: string;
  telefone: string;
}

interface ModalPedirCotacaoProps {
  card: Card;
  flowId: string;
  /** A etapa traz o modelo em `cotacaoCampos` — configurado em Configurar Etapa. */
  etapaAtual: Etapa;
  /** Todas as etapas do fluxo. O contexto do pedido quase sempre mora antes
   *  da etapa atual — numa viagem, viajante e destino ficam na solicitação. */
  etapasFluxo?: Etapa[];
  /** Quem pede cotação nesta etapa também pode gravar o modelo dela. */
  podeSalvarPadrao?: boolean;
  userEmail?: string;
  empresaId?: string;
  onClose: () => void;
  /** Disparado depois que os links nascem. */
  onCriadas?: (cotacoes: any[]) => void;
  /** Avisa o board que o padrão da etapa mudou, sem recarregar a página. */
  onPadraoSalvo?: (
    etapaId: string,
    padrao: { campos: CamposCotacaoEtapa | null; fornecedores: FornecedorCotacao[] | null }
  ) => void;
}

const vazio = (): Fornecedor => ({ nome: '', email: '', telefone: '' });

export default function ModalPedirCotacao({
  card, flowId, etapaAtual, etapasFluxo, podeSalvarPadrao,
  userEmail, empresaId, onClose, onCriadas, onPadraoSalvo,
}: ModalPedirCotacaoProps) {
  const { success, error: toastError } = useToast();

  const valores = (card.campos || {}) as Record<string, any>;
  const etapas = etapasFluxo && etapasFluxo.length ? etapasFluxo : [etapaAtual];
  const padrao = etapaAtual.cotacaoCampos;

  // A etapa guarda os fornecedores habituais: abrir em branco obrigava a
  // redigitar nome, e-mail e telefone dos mesmos de sempre.
  const daEtapa = (etapaAtual.cotacaoFornecedores || []).map(f => ({
    nome: f.nome || '', email: f.email || '', telefone: f.telefone || '',
  }));
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>(
    daEtapa.length ? daEtapa : [vazio()]
  );
  const [contexto, setContexto] = useState<string[]>([]);
  const [resposta, setResposta] = useState<string[]>([]);
  const [expiracaoDias, setExpiracaoDias] = useState<number | ''>(7);
  const [enviando, setEnviando] = useState(false);
  const [salvandoPadrao, setSalvandoPadrao] = useState(false);
  // Fornecedor sem e-mail não recebe nada: o link dele tem que aparecer aqui,
  // senão nasce e se perde junto com o modal.
  const [semEmail, setSemEmail] = useState<{ nome: string; link: string }[]>([]);
  // Cópia local do modelo: o board é atualizado sem re-render deste modal, e
  // sem isto o botão de salvar continuaria aceso depois de gravar.
  const [padraoAtual, setPadraoAtual] = useState<CamposCotacaoEtapa | undefined>(padrao);

  // Abre já marcado com o modelo da etapa. A chave é o conteúdo, não a
  // identidade: re-render do pai não pode zerar o que o usuário marcou.
  const chavePadrao = `${etapaAtual.id}|${JSON.stringify(padrao || null)}`;
  const padraoAplicado = useRef<string | null>(null);
  useEffect(() => {
    if (padraoAplicado.current === chavePadrao) return;
    padraoAplicado.current = chavePadrao;

    const existentes = new Set<string>();
    etapas.forEach(e => (e.campos || []).forEach(c => existentes.add(String(c.id))));
    const daEtapa = new Set((etapaAtual.campos || []).map(c => String(c.id)));
    const limpar = (ids?: string[], soDaEtapa = false) =>
      (ids || []).map(String).filter(id => existentes.has(id) && (!soDaEtapa || daEtapa.has(id)));

    setPadraoAtual(padrao);
    setContexto(limpar(padrao?.contexto));
    setResposta(limpar(padrao?.resposta, true));
  }, [chavePadrao, padrao, etapas, etapaAtual]);

  const alterarFornecedor = (i: number, campo: keyof Fornecedor, valor: string) => {
    setFornecedores(prev => prev.map((f, k) => (k === i ? { ...f, [campo]: valor } : f)));
  };

  const getAuthParams = () => {
    const p = new URLSearchParams();
    if (userEmail) p.set('userEmail', userEmail);
    if (empresaId) p.set('empresaId', empresaId);
    return p.toString();
  };

  const validos = fornecedores.filter(f => f.nome.trim());

  const faltaPreencher = resposta.length === 0;

  const igualAoPadrao =
    JSON.stringify([[...contexto].sort(), [...resposta].sort()]) ===
    JSON.stringify([
      [...(padraoAtual?.contexto || [])].sort(),
      [...(padraoAtual?.resposta || [])].sort(),
    ]);

  const salvarPadrao = async () => {
    try {
      setSalvandoPadrao(true);
      const res = await fetch(
        `${apiUrl(`/flows/${flowId}/cotacao/preset`)}?${getAuthParams()}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
          etapaId: String(etapaAtual.id),
          contexto,
          resposta,
          fornecedores: validos,
        }),
        }
      );
      const data = await res.json();
      if (data.success) {
        success('Padrão da etapa salvo. O próximo pedido já abre assim.');
        const salvo = contexto.length || resposta.length ? { contexto, resposta } : null;
        setPadraoAtual(salvo || undefined);
        onPadraoSalvo?.(String(etapaAtual.id), {
          campos: salvo,
          fornecedores: validos.length ? validos : null,
        });
      } else {
        toastError(data.error || 'Não consegui salvar o padrão');
      }
    } catch {
      toastError('Não consegui salvar o padrão');
    } finally {
      setSalvandoPadrao(false);
    }
  };

  const copiar = async (link: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        success('Link copiado');
        return;
      }
      throw new Error('sem clipboard');
    } catch {
      try {
        const area = document.createElement('textarea');
        area.value = link;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        if (ok) { success('Link copiado'); return; }
      } catch { /* cai no aviso abaixo */ }
      toastError('Não consegui copiar — selecione o link à mão');
    }
  };

  const enviar = async () => {
    if (validos.length === 0) return toastError('Informe ao menos um fornecedor');
    if (resposta.length === 0) return toastError('Escolha ao menos um campo para o fornecedor preencher');

    try {
      setEnviando(true);
      const res = await fetch(`${apiUrl('/cards/cotacao')}?${getAuthParams()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardId: card.id,
          flowId,
          fornecedores: validos,
          camposContexto: contexto,
          camposResposta: resposta,
          expiracaoDias: expiracaoDias || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const convites = Number(data.convitesEnviados || 0);
        const orfaos = (data.cotacoes || [])
          .filter((c: any) => !(c.fornecedor?.email || '').trim())
          .map((c: any) => ({ nome: c.fornecedor?.nome || 'Fornecedor', link: c.link }));

        success(
          `${data.cotacoes.length} link(s) de cotação criados` +
          (convites > 0 ? ` · e-mail a caminho para ${convites} fornecedor(es)` : '')
        );
        onCriadas?.(data.cotacoes);

        // Todo mundo com e-mail: o pedido já saiu, não há nada para fazer aqui.
        if (orfaos.length === 0) onClose();
        else setSemEmail(orfaos);
      } else {
        toastError(data.error || 'Erro ao criar a cotação');
      }
    } catch {
      toastError('Erro ao criar a cotação');
    } finally {
      setEnviando(false);
    }
  };

  if (semEmail.length > 0) {
    return (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
        onClick={e => {
          e.stopPropagation();
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Links gerados</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Estes fornecedores não têm e-mail cadastrado — copie o link e mande você mesmo.
              Depois de fechar, ele não aparece mais.
            </p>
          </div>
          <div className="space-y-2">
            {semEmail.map(f => (
              <div key={f.link} className="border border-gray-200 rounded-lg p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-medium text-gray-800 truncate">{f.nome}</span>
                  <button
                    onClick={() => copiar(f.link)}
                    className="text-[11px] px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                  >Copiar link</button>
                </div>
                <a
                  href={f.link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] text-blue-600 hover:underline break-all"
                >{f.link}</a>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >Fechar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      // O card por baixo fecha com qualquer clique que chegue no overlay dele.
      // Seguramos o clique aqui: só o fundo escuro fecha, e fecha só este modal.
      onClick={e => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">💰 Pedir cotação</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Cada fornecedor recebe um link próprio. O que ele preencher cai nos campos deste card.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* ── Fornecedores ── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-800">Para quem enviar</h3>
              <button
                onClick={() => setFornecedores(prev => [...prev, vazio()])}
                className="text-[11px] px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                + Fornecedor
              </button>
            </div>
            <div className="space-y-2">
              {fornecedores.map((f, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[1.2fr_1.2fr_1fr_auto] gap-2">
                  <input
                    value={f.nome}
                    onChange={e => alterarFornecedor(i, 'nome', e.target.value)}
                    placeholder="Nome do fornecedor *"
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <input
                    value={f.email}
                    onChange={e => alterarFornecedor(i, 'email', e.target.value)}
                    placeholder="E-mail (opcional)"
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <input
                    value={f.telefone}
                    onChange={e => alterarFornecedor(i, 'telefone', e.target.value)}
                    placeholder="Telefone"
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  {fornecedores.length > 1 && (
                    <button
                      onClick={() => setFornecedores(prev => prev.filter((_, k) => k !== i))}
                      className="px-2 text-gray-400 hover:text-red-500"
                      title="Remover"
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* ── Campos ── */}
          <section>
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="text-xs font-semibold text-gray-800">O que ele vê e o que ele preenche</h3>
              {padraoAtual && (contexto.length > 0 || resposta.length > 0) && igualAoPadrao && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">
                  padrão da etapa
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mb-2">
              <b>Vê</b> é contexto, só leitura — pode vir de qualquer etapa do fluxo.
              <b> Preenche</b> é o que você quer receber de volta, e só sai de <b>{etapaAtual.nome}</b>.
            </p>

            <SeletorCamposCotacao
              etapaAtual={etapaAtual}
              etapas={etapas}
              contexto={contexto}
              resposta={resposta}
              valores={valores}
              onChange={(c, r) => { setContexto(c); setResposta(r); }}
            />

            {podeSalvarPadrao && (
              <div className="flex items-center justify-between gap-3 mt-2">
                <span className="text-[10px] text-gray-500">
                  Salva campos e fornecedores como padrão desta etapa (o mesmo de
                  Configurar Etapa · Campos).
                </span>
                <button
                  onClick={salvarPadrao}
                  disabled={salvandoPadrao || igualAoPadrao}
                  className="text-[11px] px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {salvandoPadrao ? 'Salvando…' : 'Salvar como padrão da etapa'}
                </button>
              </div>
            )}
          </section>

          {/* ── Validade ── */}
          <section className="flex items-center gap-3">
            <label className="text-[11px] font-medium text-gray-700">O link expira em</label>
            <input
              type="number" min={1} max={365}
              value={expiracaoDias}
              onChange={e => setExpiracaoDias(e.target.value ? Number(e.target.value) : '')}
              className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
            <span className="text-[11px] text-gray-500">dias · em branco não expira</span>
          </section>

          <p className="text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
            Cada fornecedor recebe um endereço só dele. Quando responder, o que ele preencheu entra
            nos campos que você marcou em <b>Preenche</b>, aqui mesmo no card. Pedindo o mesmo campo
            a dois fornecedores, vale a última resposta — para comparar propostas, use um campo por
            fornecedor.
          </p>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
          {/* Botão apagado sem explicação é armadilha: diga o que falta. */}
          <span className={`text-[11px] ${faltaPreencher || validos.length === 0 ? 'text-amber-700' : 'text-gray-500'}`}>
            {validos.length === 0
              ? 'Informe o nome de ao menos um fornecedor'
              : faltaPreencher
                ? 'Marque ao menos um campo na coluna "Preenche" — é o que o fornecedor devolve'
                : `${validos.length} fornecedor(es) · ${contexto.length} de contexto · ${resposta.length} a preencher`}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancelar</button>
            <button
              onClick={enviar}
              disabled={enviando || validos.length === 0 || faltaPreencher}
              title={faltaPreencher ? 'Marque ao menos um campo em "Preenche"' : undefined}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {enviando ? 'Gerando…' : 'Gerar links'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
